/* ============================================================
   DIETMONSTA · Cart delivery date & time

   Shopify Grow can't run a Checkout UI Extension, so the delivery
   date/time picker lives here instead — in the cart drawer and the full
   cart page, both of which render this same custom element from
   dm-cart-delivery.liquid.

   Zone data (postcode -> zone, fee, lead time, time slots) is fetched
   from dm-postcodes.json, the exact file the homepage postcode checker
   (dm-delivery.js) already uses, so the two can never disagree about
   which zone a postcode belongs to.

   The working-day date math below is the same corrected rule as
   dm-delivery.js's earliestWorkingDate() — ported, not shared (these are
   two separate script entry points), so keep both in sync if the rule
   ever changes:

     A working day is Monday through Saturday; Sunday and any public
     holiday are excluded (holidays use the exact same logic as Sunday).
     The order day itself never counts — counting starts the day after,
     walking forward one calendar day at a time and only counting days
     that are working days.

   The public-holiday list lives in dm-postcodes.json (key "holidays",
   ISO YYYY-MM-DD strings, built by
   scripts/build-postcode-checker-data.py) — the same file this element
   already fetches for zone data, so the cart picker and the homepage
   checker can never disagree about which days are closed.

   Selections are saved to the cart as attributes (Delivery Postcode /
   Delivery Zone / Delivery Date / Delivery Time) via /cart/update.js —
   the same endpoint dm-cart.js / dm-cart-drawer.js already use for line
   removal, just with `attributes` instead of `updates`. Cart attributes
   surface automatically under "Additional details" on the order in
   Shopify Admin. Slot options are per zone, not per date, so once a
   zone resolves both selects show together, each defaulted to its first
   valid option — matching the client's mockup, which shows both filled
   in rather than the time field waiting on a deliberate date pick.
   ============================================================ */

/* Rendered from both dm-cart-drawer.liquid (always present, in the
   layout) and dm-cart.liquid (the /cart page) — both can be on the page
   at once, so this script tag loads twice. Guard against that instead of
   moving the <script> tag into the layout, keeping this snippet
   self-contained the same way dm-delivery.js/dm-cart.js are. */
if (!window.__dmCartDeliveryLoaded) {
window.__dmCartDeliveryLoaded = true;

const DAYS_AHEAD = 28; // how far out the date picker offers, past the earliest date
const POSTCODE_RE = /^\d{5}$/;

let dataPromise = null;
function loadData(url) {
  if (!dataPromise) {
    dataPromise = fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error(`postcode data ${res.status}`);
        return res.json();
      })
      .catch((err) => {
        // Never cache a failure: one network blip would otherwise break
        // every later postcode until the page is reloaded.
        dataPromise = null;
        throw err;
      });
  }
  return dataPromise;
}

/* Local-calendar ISO date (YYYY-MM-DD) — NOT toISOString(), which is UTC
   and would name the wrong day for a late-evening time in UTC+8. */
function localISODate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/* A working day is Mon–Sat and not a public holiday. Sunday and holidays
   are excluded by the same test. `holidays` is the array of ISO date
   strings from dm-postcodes.json. */
function isWorkingDay(date, holidays) {
  if (date.getDay() === 0) return false; // Sunday
  return !holidays.includes(localISODate(date));
}

/* The order day itself never counts as day 1 — counting starts the day
   after, and only working days are counted. */
function earliestWorkingDate(fromDate, leadDays, holidays) {
  const date = new Date(fromDate);
  let counted = 0;
  while (counted < leadDays) {
    date.setDate(date.getDate() + 1);
    if (isWorkingDay(date, holidays)) counted++;
  }
  return date;
}

function leadDaysFrom(leadText) {
  const match = /(\d+)/.exec(leadText || '');
  return match ? parseInt(match[1], 10) : null;
}

function formatDate(date) {
  return date.toLocaleDateString('en-MY', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

/* Every valid delivery date from the earliest date onward, skipping
   Sundays and public holidays, out to DAYS_AHEAD days. value === label:
   the formatted string
   is both what's shown and what's saved/matched as the cart attribute.
   iso rides along as a data attribute on the <option> (see
   #populateSelect) purely so the collapsed summary can re-render a short
   "Sat, 29 Aug" without re-parsing the long stored string. */
function validDates(earliest, holidays) {
  const out = [];
  const date = new Date(earliest);
  let added = 0;
  let guard = 0;
  while (added < DAYS_AHEAD && guard < DAYS_AHEAD * 2 + 30) {
    guard++;
    if (isWorkingDay(date, holidays)) {
      const label = formatDate(date);
      out.push({ value: label, label, iso: date.toISOString() });
      added++;
    }
    date.setDate(date.getDate() + 1);
  }
  return out;
}

function formatDateShort(iso) {
  return new Date(iso).toLocaleDateString('en-MY', { weekday: 'short', day: 'numeric', month: 'short' });
}

class DMCartDelivery extends HTMLElement {
  connectedCallback() {
    this.dataUrl = this.dataset.postcodesUrl;
    this.formEl = this.querySelector('[data-dm-cd-form]');
    this.summaryEl = this.querySelector('[data-dm-cd-summary]');
    this.summaryTextEl = this.querySelector('[data-dm-cd-summary-text]');
    this.editBtn = this.querySelector('[data-dm-cd-edit]');
    this.minimizeBtn = this.querySelector('[data-dm-cd-minimize]');
    this.postcodeInput = this.querySelector('[data-dm-cd-postcode]');
    this.errorEl = this.querySelector('[data-dm-cd-error]');
    this.resultEl = this.querySelector('[data-dm-cd-result]');
    this.checkEl = this.querySelector('[data-dm-cd-check]');
    this.zoneEl = this.querySelector('[data-dm-cd-zone]');
    this.earliestEl = this.querySelector('[data-dm-cd-earliest]');
    this.dateSelect = this.querySelector('[data-dm-cd-date-select]');
    this.timeField = this.querySelector('[data-dm-cd-time-field]');
    this.timeSelect = this.querySelector('[data-dm-cd-time-select]');

    this.editBtn.addEventListener('click', () => this.#expand());
    // The only way to collapse — there's no automatic collapse anywhere
    // in this file. Instant, not a save-then-refresh round trip: the
    // selection is already saved (every select change persists
    // immediately), so there's nothing to wait on.
    this.minimizeBtn.addEventListener('click', () => this.#collapse());

    this.currentZoneKey = null;
    this.currentZone = null;
    this.hasResolvedOnce = false;
    this.initialDate = this.dataset.initialDate || '';
    this.initialTime = this.dataset.initialTime || '';

    const initialPostcode = this.dataset.initialPostcode || '';
    if (initialPostcode) this.postcodeInput.value = initialPostcode;

    // The postcode that's actually behind the currently-saved cart
    // attributes — i.e. what the server-rendered checkout button's
    // disabled state was computed from. Only set once a resolution
    // finishes and saves; see the input listener below for why this
    // matters.
    this.resolvedPostcode = initialPostcode && this.initialDate && this.initialTime ? initialPostcode : '';

    let debounceTimer;
    this.postcodeInput.addEventListener('input', () => {
      clearTimeout(debounceTimer);
      const value = this.#postcodeValue();

      // The moment the field stops matching what's actually saved, the
      // server-rendered "Checkout" button is showing a stale answer — it
      // was enabled for the OLD postcode's delivery info, not whatever
      // this keystroke is turning into. Block it immediately rather than
      // trusting stale state for the ~350ms+fetch it takes to resolve
      // the new one; a customer who clicks Checkout mid-edit must not
      // sail through on the previous selection.
      if (value !== this.resolvedPostcode) {
        DMCartDelivery.blockCheckout();
        // Remember we did this: once the new postcode resolves, the button
        // must be re-enabled by a refresh even if the date/time ended up
        // identical to what the page loaded with.
        this.#checkoutBlocked = true;
      }

      if (!POSTCODE_RE.test(value)) {
        this.#hideResult();
        if (value.length > 5) this.#showError('Postcodes are 5 digits — please check yours.');
        else this.#hideError();
        return;
      }
      debounceTimer = setTimeout(() => this.#resolvePostcode(value), 350);
    });

    this.dateSelect.addEventListener('change', () => this.#onSelectChange());
    this.timeSelect.addEventListener('change', () => this.#onSelectChange());

    if (initialPostcode && POSTCODE_RE.test(initialPostcode)) {
      this.#resolvePostcode(initialPostcode);
    }
  }

  /* Typing a second postcode before the first one's resolve (two network
     round trips: zone data, then the cart save) has finished starts a
     second, fully independent #resolvePostcode call — nothing cancels
     the first. If that first call's await happens to settle AFTER the
     second one has already written its result, its continuation resumes
     and overwrites resolvedPostcode (and can re-trigger a notify) with
     the SUPERSEDED postcode, even though both selects and the saved cart
     attributes are already correctly showing the newer one. A monotonic
     token lets a call detect it's been superseded and bail out before
     touching any shared state, at every point another call could have
     started in the meantime. */
  #resolveToken = 0;
  #checkoutBlocked = false;
  #saveQueue = Promise.resolve();

  /* Digits only, so "58 100" or "58100 KL" from autofill or a paste still
     matches instead of silently doing nothing. */
  #postcodeValue() {
    return this.postcodeInput.value.replace(/\D/g, '');
  }

  async #onSelectChange() {
    if (!(await this.#persistCurrentSelection())) {
      this.#showSaveError();
      return;
    }
    this.#hideError();
    this.#notifyDeliveryChanged();
  }

  #showSaveError() {
    this.#showError("Couldn't save your delivery choice — check your connection and try again.");
  }

  async #resolvePostcode(postcode) {
    const token = ++this.#resolveToken;
    let zones;
    let holidays;
    try {
      const data = await loadData(this.dataUrl);
      zones = data.zones;
      holidays = data.holidays || [];
    } catch {
      if (token !== this.#resolveToken) return;
      this.#showError('Could not check delivery right now — try again shortly.');
      return;
    }
    if (token !== this.#resolveToken) return; // a newer postcode has since started resolving

    const entry = Object.entries(zones).find(([, z]) => z.postcodes.includes(postcode));
    if (!entry) {
      const hadZone = !!this.currentZoneKey;
      this.currentZoneKey = null;
      this.currentZone = null;
      this.resolvedPostcode = '';
      this.hasResolvedOnce = true;
      this.#hideResult();
      this.#showError('No delivery to that postcode yet.');
      this.#expand();
      this.#updateMinimizeVisibility();
      if (hadZone) {
        await this.#save(postcode, null, '', '');
        if (token !== this.#resolveToken) return;
        this.#notifyDeliveryChanged();
      }
      return;
    }

    const [zoneKey, zone] = entry;
    const isFirstResolution = !this.hasResolvedOnce;
    const zoneIsNew = zoneKey !== this.currentZoneKey;
    this.hasResolvedOnce = true;
    this.currentZoneKey = zoneKey;
    this.currentZone = zone;
    this.#hideError();

    const leadDays = leadDaysFrom(zone.lead);
    const earliest = earliestWorkingDate(new Date(), leadDays || 0, holidays);
    const dates = validDates(earliest, holidays);

    this.zoneEl.textContent = zone.label;
    // Short form ("Fri, 28 Aug") here specifically — this sits in a
    // narrow two-column row next to Delivery Area, and the year isn't
    // load-bearing for a date that's always within the next few weeks.
    // The full long form is still what's saved/shown in the Delivery
    // Date select and the cart attribute Admin sees.
    this.earliestEl.textContent = dates[0] ? formatDateShort(dates[0].iso) : '—';
    this.resultEl.hidden = false;
    this.checkEl.hidden = false;

    // Preserve an in-progress selection when re-checking the same zone;
    // restore a saved one on first load; otherwise (a genuine zone
    // change) start clean — the old date/time may no longer be valid.
    let dateToKeep = '';
    let timeToKeep = '';
    if (isFirstResolution) {
      dateToKeep = this.initialDate;
      timeToKeep = this.initialTime;
    } else if (!zoneIsNew) {
      dateToKeep = this.dateSelect.value;
      timeToKeep = this.timeSelect.value;
    }

    this.#populateSelect(this.dateSelect, dates, dateToKeep);
    const slots = zone.slots || [];
    this.#populateSelect(this.timeSelect, slots.map((s) => ({ value: s, label: s })), timeToKeep);
    this.timeField.hidden = slots.length === 0;

    const saved = await this.#persistCurrentSelection();
    if (token !== this.#resolveToken) return; // superseded while the save was in flight
    if (!saved) {
      this.#showSaveError();
      this.#expand();
      return;
    }
    this.resolvedPostcode = postcode;

    // Notify (drawer: refresh its section; cart page: reload) only when
    // what's now saved actually differs from what the page was rendered
    // with — i.e. this.initialDate/initialTime, the snapshot taken once
    // at connect. That's the one comparison that's actually correct here:
    // "not the first resolution" is NOT the same thing. A cart with no
    // prior delivery info completing its FIRST-ever selection is also a
    // "first resolution," but the server-rendered checkout button was
    // built from the empty state and is now stale — it needs the same
    // refresh a later change would trigger. Comparing against the
    // original snapshot instead of a first/not-first flag catches both
    // "just went from incomplete to complete" and "zone changed under an
    // existing selection," while correctly skipping a plain restore
    // (nothing actually changed from what was already rendered).
    const changedFromOriginal =
      this.dateSelect.value !== this.initialDate || this.timeSelect.value !== this.initialTime;
    if (changedFromOriginal || this.#checkoutBlocked) {
      this.#checkoutBlocked = false;
      this.#notifyDeliveryChanged();
    }

    // No auto-collapse — the form stays open (or opens, if it was showing
    // the "no delivery here" error) once resolved. Collapsing is a
    // deliberate customer action only, via the minimize control below,
    // which #updateMinimizeVisibility() shows now that there's something
    // valid to collapse into.
    this.#expand();
    this.#updateMinimizeVisibility();
  }

  /* The manual minimize control only makes sense once there's a complete,
     valid selection to collapse into a summary — hidden otherwise (still
     typing a postcode, no zone resolved, etc). */
  #updateMinimizeVisibility() {
    const complete = !!(this.currentZone && this.dateSelect.value && this.timeSelect.value);
    this.minimizeBtn.hidden = !complete;
  }

  #populateSelect(select, options, selectedValue) {
    select.innerHTML = '';
    options.forEach(({ value, label, iso }) => {
      const opt = document.createElement('option');
      opt.value = value;
      opt.textContent = label;
      if (iso) opt.dataset.iso = iso;
      select.appendChild(opt);
    });
    if (selectedValue && options.some((o) => o.value === selectedValue)) {
      select.value = selectedValue;
    } else if (options.length) {
      select.value = options[0].value;
    }
  }

  #persistCurrentSelection() {
    return this.#save(
      this.#postcodeValue(),
      this.currentZone,
      this.currentZone ? this.dateSelect.value : '',
      this.currentZone ? this.timeSelect.value : ''
    );
  }

  /* Resolves true only if Shopify accepted the save. Saves run strictly one
     after another so a slow earlier request can never land after a newer
     one and overwrite it with a stale postcode/date; a failed attempt is
     retried once. */
  #save(postcode, zone, date, time) {
    const body = JSON.stringify({
      attributes: {
        'Delivery Postcode': postcode || '',
        'Delivery Zone': zone ? zone.label : '',
        'Delivery Date': date || '',
        'Delivery Time': time || '',
      },
    });
    const run = async () => {
      for (let attempt = 0; attempt < 2; attempt++) {
        try {
          const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body,
          });
          if (res.ok) return true;
        } catch {
          /* retry once below */
        }
        if (attempt === 0) await new Promise((r) => setTimeout(r, 500));
      }
      return false;
    };
    const result = this.#saveQueue.then(run, run);
    this.#saveQueue = result.catch(() => {});
    return result;
  }

  /* Drawer: refresh its section in place (existing hook, used for every
     other cart mutation too). Full cart page: reload, matching how
     dm-cart.js already handles other cart mutations — Liquid recomputes
     the checkout button's disabled state from cart.attributes either way. */
  #notifyDeliveryChanged() {
    if (this.closest('dm-cart-drawer')) {
      document.dispatchEvent(new CustomEvent('dm:cart-updated'));
    } else {
      window.location.reload();
    }
  }

  /* Collapsing only toggles visibility — the form underneath keeps its
     values (postcode input, both selects), so Edit never has to re-fetch
     or re-ask for anything already chosen. */
  #collapse() {
    this.#buildSummaryText();
    this.formEl.hidden = true;
    this.summaryEl.hidden = false;
    this.classList.add('is-collapsed');
  }

  #expand() {
    this.summaryEl.hidden = true;
    this.formEl.hidden = false;
    this.classList.remove('is-collapsed');
  }

  #buildSummaryText() {
    const dateOpt = this.dateSelect.selectedOptions[0];
    const iso = dateOpt?.dataset.iso;
    const shortDate = iso ? formatDateShort(iso) : dateOpt?.textContent || '';
    const time = this.timeSelect.value;
    const zoneLabel = this.currentZone ? this.currentZone.label : '';
    this.summaryTextEl.textContent = `Delivery: ${shortDate} · ${time} · ${zoneLabel}`;
  }

  #showError(message) {
    this.errorEl.textContent = message;
    this.errorEl.hidden = false;
  }

  #hideError() {
    this.errorEl.hidden = true;
  }

  #hideResult() {
    this.resultEl.hidden = true;
    this.checkEl.hidden = true;
    this.minimizeBtn.hidden = true;
  }

  /* Disables every checkout button on the page immediately — the drawer's
     and the cart page's both read the same cart, so both need to stop
     trusting a delivery selection that's mid-edit. Client-side only; the
     server-rendered disabled state (from cart.attributes) takes back
     over on the next section refresh/reload once a new selection saves,
     so this never needs a matching "unblock." */
  static blockCheckout() {
    document.querySelectorAll('button[name="checkout"]').forEach((btn) => {
      btn.disabled = true;
    });
  }
}

if (!customElements.get('dm-cart-delivery')) {
  customElements.define('dm-cart-delivery', DMCartDelivery);
}

} // window.__dmCartDeliveryLoaded guard
